import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlay, FaPause, FaDownload, FaBriefcase, FaInfoCircle } from "react-icons/fa";
import profilePic from "../assets/IMG_1267.jpg";
import { ACHIEVEMENTS, MEMORIES, EXPERIENCE } from "./About";
import { PROJECTS } from "./Projects";
import { Row, Img, ProjectCard, Lightbox, ProjectModal, useReveal } from "../components/Netflix";
import { QuickRow, TopMoments, SkillUniverse, FullStory, Journey, Finale, IntroReel, ScrollFx } from "../components/Upgrade";

/* Experience logos: drop an image in src/assets/experience/ named after the entry's `logo` key */
const logos = {};
try {
  const ctx = require.context("../assets/experience", false, /\.(png|jpe?g|webp|svg)$/i);
  ctx.keys().forEach(k => { logos[k.replace(/^\.\/|\.\w+$/g, "")] = ctx(k); });
} catch (e) { /* no logos yet */ }

const hoverPlay = e => { const v = e.currentTarget.querySelector("video"); if (v) v.play().catch(() => {}); };
const hoverStop = e => { const v = e.currentTarget.querySelector("video"); if (v) { v.pause(); v.currentTime = 0; } };

/* Profiles written before the new sections existed get them slotted in automatically */
const withNewRows = order =>
  order.includes("story")
    ? order
    : [
        ...order.flatMap(k => (k === "certs" ? ["moments", k] : k === "experience" ? [k, "journey"] : [k])),
        "story",
      ];

export default function Home({ profile }) {
  const navigate = useNavigate();
  const [proj, setProj] = useState(null);
  const [lb, setLb] = useState(null);
  const [paused, setPaused] = useState(false);
  const [reel, setReel] = useState(false);
  const [expRef, expRv] = useReveal();

  useEffect(() => { document.title = "Aayam Bhattarai — Frontend Developer"; }, []);

  const rows = {
    story: <FullStory key="story" />,
    moments: <TopMoments key="moments" onOpen={setLb} />,
    skills: <SkillUniverse key="skills" />,
    journey: <Journey key="journey" />,
    projects: (
      <Row key="projects" id="projects" title="Originals: top projects" to="/projects">
        {PROJECTS.map((p, i) => (
          <div className="nx-top" key={p.id}>
            <span className="nx-num" aria-hidden="true">{i + 1}</span>
            <ProjectCard p={p} onOpen={setProj} />
          </div>
        ))}
      </Row>
    ),
    experience: (
      <section key="experience" className={`nx-row nx-eps ${expRv}`} ref={expRef} id="experience" aria-label="Experience">
        <h2>Experience</h2>
        <ol>
          {EXPERIENCE.map((e, i) => (
            <li key={e.title}>
              <span className="nx-epn">{i + 1}</span>
              <div className={`nx-epimg${logos[e.logo] ? " logo" : ""}${logos[e.logo] && e.logoZoom ? " zoom" : ""}`}>
                {logos[e.logo] ? <img src={logos[e.logo]} alt={`${e.org} logo`} /> : <FaBriefcase />}
              </div>
              <div className="nx-epb">
                <div className="nx-eph"><h3>{e.title}</h3><small>{e.year}</small></div>
                <p className="nx-org">{e.org}</p>
                <p>{e.desc}</p>
                <div className="nx-tags">{(e.tags || []).map(t => <span key={t}>{t}</span>)}</div>
              </div>
            </li>
          ))}
          <li className="nx-soon">
            <span className="nx-epn">{EXPERIENCE.length + 1}</span>
            <div className="nx-epimg"><FaPlay /></div>
            <div className="nx-epb">
              <div className="nx-eph"><h3>Next season</h3><small>Coming soon</small></div>
              <p>Open to new roles and freelance work. Want to be part of the next chapter? Let's talk.</p>
            </div>
          </li>
        </ol>
      </section>
    ),
    certs: (
      <Row key="certs" id="certs" title="Certifications">
        {ACHIEVEMENTS.map(a => (
          <button className="nx-card" key={a.title} onClick={() => setLb({ type: "image", src: a.img, alt: a.title })}>
            <Img src={a.img} alt={a.title} />
            <span className="nx-cap"><b>{a.title}</b><small>{a.year} · {a.cat}</small></span>
          </button>
        ))}
      </Row>
    ),
    memories: (
      <Row key="memories" id="memories" title="Memories: IPITEX 2026, Bangkok">
        {MEMORIES.map((m, i) => (
          <button className="nx-card" key={i}
            onMouseEnter={hoverPlay} onMouseLeave={hoverStop}
            onClick={() => setLb({ type: m.type, src: m.src, alt: m.caption || m.event })}>
            {m.type === "video"
              ? <video src={`${m.src}#t=0.1`} muted loop playsInline preload="metadata" />
              : <Img src={m.src} alt={m.caption || m.event} />}
            <span className="nx-cap"><b>{m.event}</b><small>{m.caption || "Video"}</small></span>
          </button>
        ))}
      </Row>
    ),
  };

  const order = withNewRows(profile.order);

  return (
    <>
      <ScrollFx />
      <header className={`nx-hero${paused ? " paused" : ""}`} style={{ "--photo": `url(${profilePic})` }}>
        <div className="nx-hero-bg" />
        <div className="nx-hero-in">
          <p className="nx-series"><span>A</span> Portfolio original</p>
          <h1><span className="nx-h1-a">Aayam</span> Bhattarai</h1>
          <div className="nx-stats" aria-label="At a glance">
            <span className="nx-stat"><b>{PROJECTS.length}</b> Projects</span>
            <span className="nx-stat"><b>{ACHIEVEMENTS.length}</b> Certifications</span>
            <span className="nx-stat"><b>{MEMORIES.length}</b> Expo moments</span>
          </div>
          <div className="nx-meta">
            <b className="nx-match">98% match</b>
            <span>2026</span>
            <span className="nx-pill">BSc (Hons) Computing</span>
            <span>Bhaktapur, Nepal</span>
          </div>
          <p className="nx-syn">
            Frontend developer. Whether I'm breaking down a defense on the pitch, debugging a
            stubborn script, or riding through nature's quiet paths, I'm all about passion,
            precision, and discovery.
          </p>
          <p className="nx-genres"><span>Genres:</span> Football, Coding, Nature, Data</p>
          <div className="nx-ctas">
            <button className="nx-btn nx-btn-w" onClick={() => setReel(true)}><FaPlay /> Play intro</button>
            <button className="nx-btn nx-btn-g" onClick={() => navigate("/projects")}><FaBriefcase /> View projects</button>
            <button className="nx-btn nx-btn-g" onClick={() => { const el = document.getElementById("story"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}><FaInfoCircle /> More info</button>
            <a className="nx-btn nx-btn-g" href="/Aayam_Bhattarai_CV.pdf" download="Aayam_Bhattarai_CV.pdf"><FaDownload /> Download CV</a>
          </div>
          <p className="nx-note">{profile.note}</p>
        </div>
        <div className="nx-hero-r">
          <button className="nx-round nx-round-o" onClick={() => setPaused(p => !p)}
            aria-label={paused ? "Play background motion" : "Pause background motion"}>
            {paused ? <FaPlay /> : <FaPause />}
          </button>
          <span className="nx-rate">Open to work</span>
        </div>
      </header>

      <QuickRow />

      <main className="nx-main">{order.map(k => rows[k])}</main>

      <Finale />

      {proj && <ProjectModal p={proj} all={PROJECTS} onClose={() => setProj(null)} onSelect={setProj} />}
      {lb && <Lightbox item={lb} onClose={() => setLb(null)} />}
      {reel && <IntroReel onClose={() => setReel(false)} />}
    </>
  );
}
